// src/redux/api/campaign.api.ts

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import {
  SocialCampaign,
  CampaignWithMetrics,
  CampaignAnalytics,
  CampaignMetrics,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  CampaignFilters,
  CampaignPlatform,
  DEFAULT_CAMPAIGN_METRICS,
  getPlatformConfig,
  generateCampaignSlug,
  buildTrackingUrl,
} from 'src/@types/campaign';

// Helper function to get event base URL
const getEventBaseUrl = (eventId: string): string => {
  const firebaseProject = process.env.REACT_APP_FIREBASE_PROJECT;
  const isNonProd =
    process.env.NODE_ENV === 'development' ||
    firebaseProject === 'instadrinkuat' ||
    firebaseProject === 'instadrink-47f0f';
  const baseUrl = isNonProd ? 'https://instadrinkweb-ewqf-h20webooe-aggounixs-projects.vercel.app' : 'https://www.idnetwork.io';
  return `${baseUrl}/events/book/${eventId}`;
};

export const CampaignApi = createApi({
  reducerPath: 'CampaignApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Campaign', 'CampaignAnalytics'],
  endpoints: (builder) => ({
    // Get all campaigns for an event
    getCampaigns: builder.query<CampaignWithMetrics[], { eventId: string; filters?: CampaignFilters }>({
      async queryFn({ eventId, filters }) {
        try {
          let campaignsQuery = query(
            collection(DB, 'events', eventId, 'campaigns'),
            orderBy('createdAt', 'desc')
          );

          const campaignsSnapshot = await getDocs(campaignsQuery);
          const campaigns: SocialCampaign[] = campaignsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as SocialCampaign[];

          // Apply filters
          let filteredCampaigns = campaigns;
          if (filters?.status?.length) {
            filteredCampaigns = filteredCampaigns.filter((c) => filters.status!.includes(c.status));
          }
          if (filters?.platform?.length) {
            filteredCampaigns = filteredCampaigns.filter((c) => filters.platform!.includes(c.platform));
          }
          if (filters?.contentType?.length) {
            filteredCampaigns = filteredCampaigns.filter((c) => filters.contentType!.includes(c.contentType));
          }

          // Get metrics for each campaign - use direct campaign fields as primary source
          const campaignsWithMetrics = filteredCampaigns.map((campaign) => {
            // Use the real-time metrics stored directly on the campaign document
            // These are updated by the web app's campaign tracking API
            const clicks = campaign.clicks || 0;
            const conversions = campaign.conversions || 0;
            const revenue = campaign.totalRevenue || 0;

            const metrics: CampaignMetrics = {
              clicks,
              uniqueVisitors: clicks,
              pageViews: clicks,
              sessions: clicks,
              ticketViews: 0,
              ticketAdds: 0,
              checkoutStarts: 0,
              conversions,
              revenue,
              ticketsSold: conversions,
              conversionRate: clicks > 0 ? (conversions / clicks) * 100 : 0,
              averageOrderValue: conversions > 0 ? revenue / conversions : 0,
              revenuePerClick: clicks > 0 ? revenue / clicks : 0,
            };

            return { ...campaign, metrics };
          });

          return { data: campaignsWithMetrics };
        } catch (error: any) {
          console.error('Error fetching campaigns:', error);
          return { error: error.message };
        }
      },
      providesTags: ['Campaign'],
    }),

    // Get single campaign
    getCampaign: builder.query<CampaignWithMetrics, { eventId: string; campaignId: string }>({
      async queryFn({ eventId, campaignId }) {
        try {
          const campaignDoc = await getDoc(doc(DB, 'events', eventId, 'campaigns', campaignId));

          if (!campaignDoc.exists()) {
            return { error: 'Campaign not found' };
          }

          const campaign = { id: campaignDoc.id, ...campaignDoc.data() } as SocialCampaign;

          // Use the real-time metrics stored directly on the campaign document
          const clicks = campaign.clicks || 0;
          const conversions = campaign.conversions || 0;
          const revenue = campaign.totalRevenue || 0;

          const metrics: CampaignMetrics = {
            clicks,
            uniqueVisitors: clicks,
            pageViews: clicks,
            sessions: clicks,
            ticketViews: 0,
            ticketAdds: 0,
            checkoutStarts: 0,
            conversions,
            revenue,
            ticketsSold: conversions,
            conversionRate: clicks > 0 ? (conversions / clicks) * 100 : 0,
            averageOrderValue: conversions > 0 ? revenue / conversions : 0,
            revenuePerClick: clicks > 0 ? revenue / clicks : 0,
          };

          return { data: { ...campaign, metrics } };
        } catch (error: any) {
          console.error('Error fetching campaign:', error);
          return { error: error.message };
        }
      },
      providesTags: ['Campaign'],
    }),

    // Create campaign
    createCampaign: builder.mutation<SocialCampaign, CreateCampaignRequest & { userId: string; userName?: string }>({
      async queryFn({ eventId, userId, userName, ...data }) {
        try {
          const platformConfig = getPlatformConfig(data.platform);
          const campaignSlug = generateCampaignSlug(data.name);
          const baseUrl = getEventBaseUrl(eventId);

          // Ensure slug is never empty
          if (!campaignSlug) {
            throw new Error('Failed to generate campaign slug from name');
          }

          const utmParams = {
            utm_source: data.platform,
            utm_medium: data.medium || platformConfig.defaultMedium,
            utm_campaign: campaignSlug,
            utm_content: data.contentType,
          };

          const trackingLink = buildTrackingUrl(baseUrl, utmParams);

          const campaignData: Omit<SocialCampaign, 'id'> = {
            eventId,
            name: data.name,
            slug: campaignSlug, // Store slug for matching with UTM campaign param
            description: data.description,
            status: 'draft',
            platform: data.platform,
            contentType: data.contentType,
            utmParams,
            baseUrl,
            trackingLink,
            budget: data.budget,
            targetClicks: data.targetClicks,
            targetConversions: data.targetConversions,
            startDate: data.startDate ? Timestamp.fromDate(data.startDate) : undefined,
            endDate: data.endDate ? Timestamp.fromDate(data.endDate) : undefined,
            notes: data.notes,
            tags: data.tags,
            // Tracking metrics (initialized to 0)
            clicks: 0,
            conversions: 0,
            totalRevenue: 0,
            createdBy: userId,
            createdByName: userName,
            createdAt: serverTimestamp() as Timestamp,
            updatedAt: serverTimestamp() as Timestamp,
          };

          const docRef = await addDoc(collection(DB, 'events', eventId, 'campaigns'), campaignData);

          return {
            data: {
              id: docRef.id,
              ...campaignData,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            },
          };
        } catch (error: any) {
          console.error('Error creating campaign:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['Campaign', 'CampaignAnalytics'],
    }),

    // Update campaign
    updateCampaign: builder.mutation<void, UpdateCampaignRequest>({
      async queryFn({ campaignId, eventId, updates }) {
        try {
          // If name changed, regenerate UTM campaign slug and tracking link
          let finalUpdates = { ...updates };

          if (updates.name) {
            const campaignDoc = await getDoc(doc(DB, 'events', eventId, 'campaigns', campaignId));
            const existingCampaign = campaignDoc.data() as SocialCampaign;

            const newSlug = generateCampaignSlug(updates.name);
            const newUtmParams = {
              ...existingCampaign.utmParams,
              utm_campaign: newSlug,
            };
            const newTrackingLink = buildTrackingUrl(existingCampaign.baseUrl, newUtmParams);

            finalUpdates = {
              ...finalUpdates,
              slug: newSlug, // Update slug field for web app tracking lookup
              utmParams: newUtmParams,
              trackingLink: newTrackingLink,
            };
          }

          await updateDoc(doc(DB, 'events', eventId, 'campaigns', campaignId), {
            ...finalUpdates,
            updatedAt: serverTimestamp(),
          });

          return { data: undefined };
        } catch (error: any) {
          console.error('Error updating campaign:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['Campaign', 'CampaignAnalytics'],
    }),

    // Delete campaign
    deleteCampaign: builder.mutation<void, { eventId: string; campaignId: string }>({
      async queryFn({ eventId, campaignId }) {
        try {
          await deleteDoc(doc(DB, 'events', eventId, 'campaigns', campaignId));
          return { data: undefined };
        } catch (error: any) {
          console.error('Error deleting campaign:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['Campaign', 'CampaignAnalytics'],
    }),

    // Update campaign status
    updateCampaignStatus: builder.mutation<void, { eventId: string; campaignId: string; status: SocialCampaign['status'] }>({
      async queryFn({ eventId, campaignId, status }) {
        try {
          await updateDoc(doc(DB, 'events', eventId, 'campaigns', campaignId), {
            status,
            updatedAt: serverTimestamp(),
          });
          return { data: undefined };
        } catch (error: any) {
          console.error('Error updating campaign status:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['Campaign'],
    }),

    // Get campaign analytics overview
    getCampaignAnalytics: builder.query<CampaignAnalytics, { eventId: string; dateRange?: { start: Date; end: Date } }>({
      async queryFn({ eventId, dateRange }) {
        try {
          // Get all campaigns
          const campaignsSnapshot = await getDocs(
            query(collection(DB, 'events', eventId, 'campaigns'), orderBy('createdAt', 'desc'))
          );

          const campaigns = campaignsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as SocialCampaign[];

          // Get metrics for all campaigns using direct campaign document fields
          const campaignsWithMetrics = campaigns.map((campaign) => {
            const clicks = campaign.clicks || 0;
            const conversions = campaign.conversions || 0;
            const revenue = campaign.totalRevenue || 0;

            const metrics: CampaignMetrics = {
              clicks,
              uniqueVisitors: clicks,
              pageViews: clicks,
              sessions: clicks,
              ticketViews: 0,
              ticketAdds: 0,
              checkoutStarts: 0,
              conversions,
              revenue,
              ticketsSold: conversions,
              conversionRate: clicks > 0 ? (conversions / clicks) * 100 : 0,
              averageOrderValue: conversions > 0 ? revenue / conversions : 0,
              revenuePerClick: clicks > 0 ? revenue / clicks : 0,
            };

            return { ...campaign, metrics };
          });

          // Calculate platform performance
          const platformStats: Record<CampaignPlatform, { campaigns: number; clicks: number; conversions: number; revenue: number; budget: number }> = {} as any;

          campaignsWithMetrics.forEach((campaign) => {
            if (!platformStats[campaign.platform]) {
              platformStats[campaign.platform] = { campaigns: 0, clicks: 0, conversions: 0, revenue: 0, budget: 0 };
            }
            platformStats[campaign.platform].campaigns++;
            platformStats[campaign.platform].clicks += campaign.metrics.clicks;
            platformStats[campaign.platform].conversions += campaign.metrics.conversions;
            platformStats[campaign.platform].revenue += campaign.metrics.revenue;
            platformStats[campaign.platform].budget += campaign.budget || 0;
          });

          const platformPerformance = Object.entries(platformStats).map(([platform, stats]) => ({
            platform: platform as CampaignPlatform,
            ...stats,
            conversionRate: stats.clicks > 0 ? (stats.conversions / stats.clicks) * 100 : 0,
            roi: stats.budget > 0 ? ((stats.revenue - stats.budget) / stats.budget) * 100 : undefined,
          }));

          // Calculate content type performance
          const contentTypeStats: Record<string, { campaigns: number; clicks: number; conversions: number; revenue: number }> = {};

          campaignsWithMetrics.forEach((campaign) => {
            if (!contentTypeStats[campaign.contentType]) {
              contentTypeStats[campaign.contentType] = { campaigns: 0, clicks: 0, conversions: 0, revenue: 0 };
            }
            contentTypeStats[campaign.contentType].campaigns++;
            contentTypeStats[campaign.contentType].clicks += campaign.metrics.clicks;
            contentTypeStats[campaign.contentType].conversions += campaign.metrics.conversions;
            contentTypeStats[campaign.contentType].revenue += campaign.metrics.revenue;
          });

          const contentTypePerformance = Object.entries(contentTypeStats).map(([contentType, stats]) => ({
            contentType: contentType as any,
            ...stats,
            conversionRate: stats.clicks > 0 ? (stats.conversions / stats.clicks) * 100 : 0,
          }));

          // Calculate totals
          const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
          const totalClicks = campaignsWithMetrics.reduce((sum, c) => sum + c.metrics.clicks, 0);
          const totalConversions = campaignsWithMetrics.reduce((sum, c) => sum + c.metrics.conversions, 0);
          const totalRevenue = campaignsWithMetrics.reduce((sum, c) => sum + c.metrics.revenue, 0);

          // Top campaigns by revenue
          const topCampaigns = [...campaignsWithMetrics]
            .sort((a, b) => b.metrics.revenue - a.metrics.revenue)
            .slice(0, 5);

          const analytics: CampaignAnalytics = {
            eventId,
            dateRange: {
              start: dateRange?.start || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
              end: dateRange?.end || new Date(),
            },
            totalCampaigns: campaigns.length,
            activeCampaigns: campaigns.filter((c) => c.status === 'active').length,
            totalBudget,
            totalSpend: totalBudget, // TODO: Track actual spend separately
            totalRevenue,
            overallROI: totalBudget > 0 ? ((totalRevenue - totalBudget) / totalBudget) * 100 : 0,
            totalClicks,
            totalConversions,
            averageConversionRate: totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0,
            platformPerformance: platformPerformance.sort((a, b) => b.revenue - a.revenue),
            contentTypePerformance: contentTypePerformance.sort((a, b) => b.revenue - a.revenue),
            topCampaigns,
            dailyStats: [], // TODO: Implement daily stats aggregation
          };

          return { data: analytics };
        } catch (error: any) {
          console.error('Error fetching campaign analytics:', error);
          return { error: error.message };
        }
      },
      providesTags: ['CampaignAnalytics'],
    }),
  }),
});

// Helper function to get metrics for a specific campaign from analytics
async function getCampaignMetrics(
  eventId: string,
  utmCampaign: string,
  dateRange?: { start: Date; end: Date }
): Promise<CampaignMetrics> {
  try {
    const endDate = dateRange?.end || new Date();
    const startDate = dateRange?.start || new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Query analytics events with this utm_campaign
    const eventsQuery = query(
      collection(DB, 'analytics', 'events', 'raw'),
      where('eventId', '==', eventId),
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<=', Timestamp.fromDate(endDate))
    );

    const eventsSnapshot = await getDocs(eventsQuery);
    const events = eventsSnapshot.docs.map((doc) => doc.data());

    // Filter events by utm_campaign
    const campaignEvents = events.filter((event) => {
      const utmParams = event.referralData?.utmParameters;
      return utmParams?.utm_campaign === utmCampaign;
    });

    if (campaignEvents.length === 0) {
      return DEFAULT_CAMPAIGN_METRICS;
    }

    // Calculate metrics
    const uniqueSessions = new Set<string>();
    const uniqueUsers = new Set<string>();
    let pageViews = 0;
    let ticketViews = 0;
    let ticketAdds = 0;
    let checkoutStarts = 0;
    let conversions = 0;
    let revenue = 0;
    let ticketsSold = 0;

    campaignEvents.forEach((event) => {
      uniqueSessions.add(event.sessionId);
      if (event.userId) {
        uniqueUsers.add(event.userId);
      } else {
        uniqueUsers.add(event.sessionId);
      }

      switch (event.eventType) {
        case 'page_view':
          pageViews++;
          break;
        case 'ticket_view':
          ticketViews++;
          break;
        case 'ticket_add':
          ticketAdds++;
          break;
        case 'checkout_start':
          checkoutStarts++;
          break;
        case 'checkout_complete':
          conversions++;
          revenue += event.metadata?.totalAmount || 0;
          ticketsSold += event.metadata?.ticketCount || 0;
          break;
      }
    });

    const clicks = uniqueSessions.size;
    const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
    const averageOrderValue = conversions > 0 ? revenue / conversions : 0;
    const revenuePerClick = clicks > 0 ? revenue / clicks : 0;

    return {
      clicks,
      uniqueVisitors: uniqueUsers.size,
      pageViews,
      sessions: uniqueSessions.size,
      ticketViews,
      ticketAdds,
      checkoutStarts,
      conversions,
      revenue,
      ticketsSold,
      conversionRate,
      averageOrderValue,
      revenuePerClick,
    };
  } catch (error) {
    console.error('Error getting campaign metrics:', error);
    return DEFAULT_CAMPAIGN_METRICS;
  }
}

export const {
  useGetCampaignsQuery,
  useGetCampaignQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useUpdateCampaignStatusMutation,
  useGetCampaignAnalyticsQuery,
} = CampaignApi;
